import { cn } from '@mds/shared/lib/utils';

import PackageCard, { packageCardsWidth, type PackageCardProps } from '@/components/card/package-card';
import PackageRow, { PACKAGE_ROW_LIST } from '@/components/card/package-row';
import DeskWorkerIllustration from '@/components/illustration/desk-worker-illustration';

/** The most cards one row holds. From this many on, the illustration goes. */
const PER_ROW = 5;

/** A card's width when five fill the band: a fifth of it, less the four gaps. */
const FIFTH_WIDTH = `calc((100% - ${PER_ROW - 1} * var(--spacing-card)) / ${PER_ROW})`;

/**
 * A list of packages, laid out from how many there are, so the API decides
 * the layout and no page caps it.
 *
 * Below `lg`, `PackageRow`s that open in place. From `lg`, the flip cards:
 *
 * - Fewer than five: one row of cards in their own grid, the illustration
 *   beside it (not in it) in what is left.
 * - Five or more: no illustration. As few rows as five-across allows, with the
 *   cards spread evenly over them (six is three and three, seven is four and
 *   three) so no row is a lone leftover. Cards stay a fifth of the band wide,
 *   and a row of fewer is centred.
 *
 * Both layouts are rendered and CSS shows one, so the switch needs no resize
 * listener.
 */
export default function PackageList({
  packages,
}: {
  packages: { key: string; props: PackageCardProps }[];
}) {
  if (packages.length === 0) return null;

  const rows = Math.ceil(packages.length / PER_ROW);
  const perRow = Math.ceil(packages.length / rows);
  const cards = packages.map(({ key, props }) => <PackageCard key={key} {...props} />);

  return (
    <>
      <div className={cn(PACKAGE_ROW_LIST, 'lg:hidden')}>
        {packages.map(({ key, props }) => (
          <PackageRow key={key} {...props} />
        ))}
      </div>

      {packages.length < PER_ROW ? (
        // The illustration adds no height, so the cards still set the row.
        <div className="hidden gap-card lg:flex">
          <div
            className="grid shrink-0 auto-cols-fr grid-flow-col gap-card"
            style={{ width: packageCardsWidth(packages.length) }}
          >
            {cards}
          </div>
          <DeskWorkerIllustration className="min-w-0 flex-1" />
        </div>
      ) : (
        <div
          // `auto-rows-fr` so a card on the second row is as tall as one on the first.
          className="hidden auto-rows-fr justify-center gap-card lg:grid"
          style={{ gridTemplateColumns: `repeat(${perRow}, ${FIFTH_WIDTH})` }}
        >
          {cards}
        </div>
      )}
    </>
  );
}
